import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, Gift } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { money, scrollToId } from "../lib/format";

export default function CartDrawer({ settings }) {
  const { items, open, setOpen, updateQty, removeItem, clear, total, count } = useCart();
  const [step, setStep] = useState("cart"); // cart | form
  const [form, setForm] = useState({ name: "", phone: "", address: "", delivery: null });
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null); // {code, discount}
  const [couponError, setCouponError] = useState("");
  const currency = settings?.currency || "RD$";

  const discount = coupon?.discount || 0;

  const shippingBase = Number(settings?.shipping_cost) || 0;
  const freeShipThreshold = Number(settings?.free_shipping_threshold) || 0;
  const qualifiesFreeShip = freeShipThreshold > 0 && total >= freeShipThreshold;
  const shipping = form.delivery === true && !qualifiesFreeShip ? shippingBase : 0;
  const finalTotal = Math.max(0, total - discount) + shipping;

  const giftThreshold = Number(settings?.free_gift_threshold) || 0;
  const giftText = settings?.free_gift_text || "una muestra de regalo";
  const giftUnlocked = giftThreshold > 0 && total >= giftThreshold;
  const giftRemaining = Math.max(0, giftThreshold - total);

  const applyCoupon = async () => {
    setCouponError("");
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", {
        code: couponCode,
        subtotal: total,
      });
      setCoupon({ code: data.code, discount: data.discount });
      toast.success(`Cupón ${data.code} aplicado`);
    } catch (err) {
      setCoupon(null);
      setCouponError(err.response?.data?.detail || "Cupón no válido");
    }
  };

  const buildMessage = () => {
    let msg = `🌸 *NUEVO PEDIDO — MO by Vir*\n\n`;
    msg += `*Cliente:* ${form.name}\n`;
    msg += `*Teléfono:* ${form.phone}\n`;
    msg += `*Dirección:* ${form.address}\n`;
    msg += `*¿Delivery?:* ${form.delivery ? "Sí" : "No"}\n\n`;
    msg += `*Pedido:*\n`;
    items.forEach((i) => {
      msg += `• ${i.quantity} × ${i.name} — ${money(i.price * i.quantity, currency)}\n`;
    });
    msg += `\n*Subtotal: ${money(total, currency)}*\n`;
    if (coupon) {
      msg += `*Cupón ${coupon.code}: -${money(discount, currency)}*\n`;
    }
    if (form.delivery === true) {
      msg += `*Envío: ${shipping === 0 ? "Gratis" : money(shipping, currency)}*\n`;
    }
    msg += `*TOTAL: ${money(finalTotal, currency)}*\n\n`;
    if (giftUnlocked) {
      msg += `🎁 *Regalo incluido: ${giftText}*\n\n`;
    }
    msg += `_Enviado desde la tienda online MO by Vir_`;
    return msg;
  };

  const sendOrder = async () => {
    if (!form.name || !form.phone || !form.address || form.delivery === null) {
      toast.error("Completa todos los campos y elige opción de delivery.");
      return;
    }
    // Log order (non-blocking for UX)
    try {
      await api.post("/orders", {
        customer_name: form.name,
        phone: form.phone,
        address: form.address,
        delivery: !!form.delivery,
        items: items.map((i) => ({
          id: i.id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
        })),
        total: finalTotal,
      });
    } catch (err) { console.error("No se pudo registrar el pedido web:", err); }

    const url = `https://wa.me/${settings?.whatsapp_number}?text=${encodeURIComponent(
      buildMessage()
    )}`;
    window.open(url, "_blank");
    toast.success("¡Abriendo WhatsApp con tu orden!");
    clear();
    setStep("cart");
    setForm({ name: "", phone: "", address: "", delivery: null });
    setCoupon(null);
    setCouponCode("");
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[70] bg-espresso/40 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
            className="fixed top-0 right-0 z-[80] h-full w-full sm:w-[440px] bg-cream flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-line">
              <span className="font-serif text-2xl">
                {step === "cart" ? "Tu carrito" : "Tus datos"}
              </span>
              <button
                data-testid="cart-close"
                onClick={() => setOpen(false)}
                className="text-espresso hover:text-rose transition-colors"
              >
                <X size={22} />
              </button>
            </div>

            {/* Empty */}
            {count === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
                <ShoppingBag size={40} strokeWidth={1} className="text-taupe" />
                <p className="font-serif text-xl">Tu carrito está vacío</p>
                <p className="text-taupe text-sm font-light">
                  Descubre nuestros mini perfumes y accesorios.
                </p>
                <button
                  data-testid="cart-empty-shop"
                  onClick={() => {
                    setOpen(false);
                    scrollToId("catalogo");
                  }}
                  className="mt-2 bg-espresso text-cream rounded-full px-7 py-3 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors"
                >
                  Ver catálogo
                </button>
              </div>
            ) : step === "cart" ? (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
                  {items.map((i) => (
                    <div
                      key={i.id}
                      data-testid={`cart-item-${i.id}`}
                      className="flex gap-4"
                    >
                      <div className="w-20 h-24 bg-surface overflow-hidden shrink-0">
                        <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 flex flex-col justify-between py-1">
                        <div className="flex justify-between gap-2">
                          <h4 className="font-serif text-lg leading-tight">{i.name}</h4>
                          <button
                            data-testid={`cart-remove-${i.id}`}
                            onClick={() => removeItem(i.id)}
                            className="text-taupe hover:text-rose transition-colors"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 border border-line rounded-full px-2 py-1">
                            <button
                              data-testid={`cart-dec-${i.id}`}
                              onClick={() => updateQty(i.id, -1)}
                              className="text-espresso hover:text-rose"
                            >
                              <Minus size={13} />
                            </button>
                            <span className="text-sm w-4 text-center">{i.quantity}</span>
                            <button
                              data-testid={`cart-inc-${i.id}`}
                              onClick={() => updateQty(i.id, 1)}
                              className="text-espresso hover:text-rose"
                            >
                              <Plus size={13} />
                            </button>
                          </div>
                          <span className="text-sm font-medium">
                            {money(i.price * i.quantity, currency)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-line px-6 py-5 space-y-4">
                  {giftThreshold > 0 && (
                    <div
                      data-testid="gift-banner"
                      className={`flex items-start gap-2.5 p-3 rounded-lg text-xs font-light ${
                        giftUnlocked ? "bg-rose/10 text-espresso" : "bg-surface text-taupe"
                      }`}
                    >
                      <Gift size={16} className={giftUnlocked ? "text-rose shrink-0 mt-0.5" : "text-taupe shrink-0 mt-0.5"} />
                      {giftUnlocked ? (
                        <span>¡Ganaste <strong className="text-rose">{giftText}</strong>! Se incluye con tu pedido.</span>
                      ) : (
                        <span>Agrega <strong className="text-espresso">{money(giftRemaining, currency)}</strong> más y recibe {giftText}.</span>
                      )}
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-taupe uppercase text-[11px] tracking-[0.2em]">
                      Total
                    </span>
                    <span className="font-serif text-2xl">{money(total, currency)}</span>
                  </div>
                  <button
                    data-testid="cart-checkout-btn"
                    onClick={() => setStep("form")}
                    className="w-full bg-espresso text-cream rounded-full py-4 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors"
                  >
                    Continuar al pedido
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-taupe">
                      Nombre y Apellido *
                    </label>
                    <input
                      data-testid="checkout-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full mt-2 bg-transparent border-b border-line py-3 focus:outline-none focus:border-rose transition-colors font-light"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-taupe">
                      Teléfono de contacto *
                    </label>
                    <input
                      data-testid="checkout-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full mt-2 bg-transparent border-b border-line py-3 focus:outline-none focus:border-rose transition-colors font-light"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-taupe">
                      Dirección de entrega *
                    </label>
                    <textarea
                      data-testid="checkout-address"
                      rows={2}
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="w-full mt-2 bg-transparent border-b border-line py-3 focus:outline-none focus:border-rose transition-colors resize-none font-light"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-taupe">
                      ¿Necesita Delivery? *
                    </label>
                    <div className="flex gap-3 mt-3">
                      {[
                        { v: true, l: "Sí" },
                        { v: false, l: "No" },
                      ].map((o) => (
                        <button
                          key={o.l}
                          data-testid={`checkout-delivery-${o.l === "Sí" ? "yes" : "no"}`}
                          onClick={() => setForm({ ...form, delivery: o.v })}
                          className={`flex-1 py-3 rounded-full text-xs uppercase tracking-[0.2em] border transition-colors ${
                            form.delivery === o.v
                              ? "bg-espresso text-cream border-espresso"
                              : "border-line text-taupe hover:border-espresso"
                          }`}
                        >
                          {o.l}
                        </button>
                      ))}
                    </div>
                    {shippingBase > 0 && (
                      <p className="text-xs text-taupe mt-2 font-light">
                        {freeShipThreshold > 0 ? (
                          <>Envío {money(shippingBase, currency)} · gratis en compras desde {money(freeShipThreshold, currency)}</>
                        ) : (
                          <>Costo de envío: {money(shippingBase, currency)}</>
                        )}
                      </p>
                    )}
                  </div>

                  {giftUnlocked && (
                    <div className="flex items-center gap-2.5 p-3 rounded-lg bg-rose/10 text-espresso text-xs font-light">
                      <Gift size={16} className="text-rose shrink-0" />
                      <span>Incluiremos <strong className="text-rose">{giftText}</strong> con tu pedido 🎁</span>
                    </div>
                  )}
                  <div className="bg-surface p-4 rounded-lg text-xs text-taupe font-light leading-relaxed">
                    Al enviar, recibiremos tu orden por WhatsApp. Validaremos disponibilidad y
                    te enviaremos el <strong className="text-espresso">link de pago</strong> o
                    coordinaremos <strong className="text-espresso">pago en efectivo</strong>.
                  </div>
                </div>

                <div className="border-t border-line px-6 py-5 space-y-3">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-taupe">
                      ¿Tienes un cupón?
                    </label>
                    <div className="flex gap-2 mt-2">
                      <input
                        data-testid="coupon-input"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="CÓDIGO"
                        className="flex-1 bg-transparent border border-line rounded-full px-4 py-2.5 text-sm tracking-widest focus:outline-none focus:border-rose transition-colors"
                      />
                      <button
                        data-testid="apply-coupon-btn"
                        onClick={applyCoupon}
                        className="px-5 rounded-full bg-espresso text-cream text-[11px] uppercase tracking-[0.15em] hover:bg-rose transition-colors"
                      >
                        Aplicar
                      </button>
                    </div>
                    {couponError && (
                      <p data-testid="coupon-error" className="text-xs text-rose mt-2">{couponError}</p>
                    )}
                  </div>

                  <div className="space-y-1.5 pt-1">
                    <div className="flex justify-between text-sm text-taupe">
                      <span>Subtotal</span>
                      <span>{money(total, currency)}</span>
                    </div>
                    {coupon && (
                      <div data-testid="cart-discount" className="flex justify-between text-sm text-rose">
                        <span>Cupón {coupon.code}</span>
                        <span>−{money(discount, currency)}</span>
                      </div>
                    )}
                    {form.delivery === true && (
                      <div data-testid="cart-shipping" className="flex justify-between text-sm text-taupe">
                        <span>Envío</span>
                        <span>{shipping === 0 ? "Gratis" : money(shipping, currency)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-taupe uppercase text-[11px] tracking-[0.2em]">Total</span>
                      <span data-testid="cart-final-total" className="font-serif text-2xl">
                        {money(finalTotal, currency)}
                      </span>
                    </div>
                  </div>

                  <button
                    data-testid="send-whatsapp-order"
                    onClick={sendOrder}
                    className="w-full bg-rose text-cream rounded-full py-4 text-xs uppercase tracking-[0.2em] hover:bg-rose-hover transition-colors flex items-center justify-center gap-2"
                  >
                    Enviar Orden por WhatsApp
                  </button>
                  <button
                    data-testid="checkout-back"
                    onClick={() => setStep("cart")}
                    className="w-full text-taupe text-[11px] uppercase tracking-[0.2em] hover:text-espresso transition-colors"
                  >
                    ← Volver al carrito
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
